import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import { User, RoleType, UserTypeLocation } from './userTypes';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Users',
    description: 'Manage your users database',
    permissionModuleKey: 'users',
    route: {
      list: '/admin/users',
      new: '/admin/users/new',
      detail: (id: number) => `/admin/users/${id}`,
      edit: (id: number) => `/admin/users/${id}/edit`},
    // API Endpoints
    api: {
      list: '/users',
      detail: (id: number) => `/users/${id}`}},

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},

  // Search Settings
  search: {
    placeholder: 'Search users by email, name, or display name...'},

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'userEmail', 'userFirstName', 'userLastName', 'userDisplayName', 'userIsActive', 'role', 'typeLocation', 'createdAt', 'updatedAt'
      ]},
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'userEmail',
        label: 'Email',
        placeholder: 'All Email',
        getValue: (item: User) => item.userEmail,
        options: []},
      {
        key: 'userFirstName',
        label: 'First Name',
        placeholder: 'All First Name',
        getValue: (item: User) => item.userFirstName,
        options: []},
      {
        key: 'userLastName',
        label: 'Last Name',
        placeholder: 'All Last Name',
        getValue: (item: User) => item.userLastName,
        options: []},
      {
        key: 'userDisplayName',
        label: 'Display Name',
        placeholder: 'All Display Name',
        getValue: (item: User) => item.userDisplayName,
        options: []},
      {
        key: 'userIsActive',
        label: 'Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: User) => item.userIsActive ? 'Active' : 'Inactive',
        options: ['Active', 'Inactive']},
      {
        key: 'role',
        label: 'Role',
        placeholder: 'All Roles',
        type: 'select' as const,
        getValue: (item: User) => item.role || '',
        options: ['ADMIN', 'USER', 'CLIENT', 'GUEST']},
      {
        key: 'typeLocation',
        label: 'Type',
        placeholder: 'All types',
        type: 'select' as const,
        getValue: (item: User) => item.typeLocation || '',
        options: ['OFFICE', 'VESSEL', 'SUPPLIER', 'MANNING_AGENT']},
      {
        key: 'createdAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: User) =>
          item.createdAt
            ? formatDateGlobal(item.createdAt)
            : undefined,
        options: []},
      {
        key: 'updatedAt',
        label: 'Updated At',
        placeholder: 'All Updated At',
        type: 'date' as const,
        getValue: (item: User) =>
          item.updatedAt
            ? formatDateGlobal(item.updatedAt)
            : undefined,
        options: []},
    ],
    // Filter logic functions
    applyFilter: (
      items: User[],
      filters: {
        userEmail: string[] | string;
        userFirstName: string[] | string;
        userLastName: string[] | string;
        userDisplayName: string[] | string;
        userIsActive: string[];
        role: string[];
        typeLocation: string[];
        createdAt: string[];
        updatedAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Email filter
        const emailFilter = filters.userEmail;
        if (typeof emailFilter === 'string' && emailFilter.trim() !== '') {
          const value = (item.userEmail || '').toLowerCase();
          if (!value.includes(emailFilter.toLowerCase())) return false;
        } else if (Array.isArray(emailFilter) && emailFilter.length > 0) {
          const value = (item.userEmail || '').toLowerCase();
          const matches = emailFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // First Name filter
        const firstNameFilter = filters.userFirstName;
        if (typeof firstNameFilter === 'string' && firstNameFilter.trim() !== '') {
          const value = (item.userFirstName || '').toLowerCase();
          if (!value.includes(firstNameFilter.toLowerCase())) return false;
        } else if (Array.isArray(firstNameFilter) && firstNameFilter.length > 0) {
          const value = (item.userFirstName || '').toLowerCase();
          const matches = firstNameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Last Name filter
        const lastNameFilter = filters.userLastName;
        if (typeof lastNameFilter === 'string' && lastNameFilter.trim() !== '') {
          const value = (item.userLastName || '').toLowerCase();
          if (!value.includes(lastNameFilter.toLowerCase())) return false;
        } else if (Array.isArray(lastNameFilter) && lastNameFilter.length > 0) {
          const value = (item.userLastName || '').toLowerCase();
          const matches = lastNameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Display Name filter
        const displayNameFilter = filters.userDisplayName;
        if (typeof displayNameFilter === 'string' && displayNameFilter.trim() !== '') {
          const value = (item.userDisplayName || '').toLowerCase();
          if (!value.includes(displayNameFilter.toLowerCase())) return false;
        } else if (Array.isArray(displayNameFilter) && displayNameFilter.length > 0) {
          const value = (item.userDisplayName || '').toLowerCase();
          const matches = displayNameFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }

        // Active status filter
        if (filters.userIsActive && filters.userIsActive.length > 0) {
          const status = item.userIsActive ? 'Active' : 'Inactive';
          if (!filters.userIsActive.includes(status)) return false;
        }

        // Role filter
        if (filters.role && filters.role.length > 0) {
          if (!filters.role.includes(item.role)) return false;
        }

        // Type filter
        if (filters.typeLocation && filters.typeLocation.length > 0) {
          if (!filters.typeLocation.includes(item.typeLocation)) return false;
        }

        // Created At filter
        if (filters.createdAt && filters.createdAt.length > 0) {
          if (!item.createdAt) {
            return false;
          }
          
          const itemDate = new Date(item.createdAt);
          const itemDateUTC = new Date(Date.UTC(
            itemDate.getUTCFullYear(),
            itemDate.getUTCMonth(),
            itemDate.getUTCDate()
          ));
          
          if (ranges.createdAt && (ranges.createdAt.start || ranges.createdAt.end)) {
            const range = ranges.createdAt;
            
            if (range.start) {
              const startDateUTC = new Date(Date.UTC(
                range.start.getUTCFullYear(),
                range.start.getUTCMonth(),
                range.start.getUTCDate()
              ));
              if (itemDateUTC < startDateUTC) {
                return false;
              }
            }
            
            if (range.end) {
              const endDateUTC = new Date(Date.UTC(
                range.end.getUTCFullYear(),
                range.end.getUTCMonth(),
                range.end.getUTCDate(),
                23, 59, 59, 999
              ));
              if (itemDateUTC > endDateUTC) {
                return false;
              }
            }
          } else {
            const value = formatDateGlobal(item.createdAt);
            const matches = filters.createdAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) {
              return false;
            }
          }
        }

        // Updated At filter
        if (filters.updatedAt && filters.updatedAt.length > 0) {
          if (!item.updatedAt) {
            return false;
          }
          
          const itemDate = new Date(item.updatedAt);
          const itemDateUTC = new Date(Date.UTC(
            itemDate.getUTCFullYear(),
            itemDate.getUTCMonth(),
            itemDate.getUTCDate()
          ));
          
          if (ranges.updatedAt && (ranges.updatedAt.start || ranges.updatedAt.end)) {
            const range = ranges.updatedAt;
            
            if (range.start) {
              const startDateUTC = new Date(Date.UTC(
                range.start.getUTCFullYear(),
                range.start.getUTCMonth(),
                range.start.getUTCDate()
              ));
              if (itemDateUTC < startDateUTC) {
                return false;
              }
            }
            
            if (range.end) {
              const endDateUTC = new Date(Date.UTC(
                range.end.getUTCFullYear(),
                range.end.getUTCMonth(),
                range.end.getUTCDate(),
                23, 59, 59, 999
              ));
              if (itemDateUTC > endDateUTC) {
                return false;
              }
            }
          } else {
            const value = formatDateGlobal(item.updatedAt);
            const matches = filters.updatedAt.some((filter) => {
              return value.includes(filter) || filter.includes(value);
            });
            if (!matches) {
              return false;
            }
          }
        }

        return true;
      });
    }},

  // Toolbar Actions Configuration
  toolbar: {
    actions: [
      {
        key: 'download',
        icon: 'download',
        title: 'Download',
        showTitle: false,
        variant: 'default' as const,
        dropdown: [
          {
            key: 'export-excel',
            label: 'Export to Excel',
            icon: 'download',
            onClick: async (exportData?: {
              sortedData: any[];
              tableColumns: any[];
              entityName: string;
              exportFileName: string;
              formatDate?: (dateString?: string) => string;
            }) => {
              if (!exportData) {
                console.error('Export data not provided');
                return;
              }

              try {
                const { exportToExcel } = await import('../../../components/exports');
                await exportToExcel(exportData);
              } catch (error: any) {
                console.error('Failed to export to Excel:', error);
                throw new Error(error?.message || 'Failed to export to Excel. Please check the console for details.');
              }
            }},
        ]},
      {
        key: 'add',
        icon: 'add',
        title: 'Add New User',
        showTitle: false,
        variant: 'primary' as const,
        href: '/admin/users/new'},
    ]},

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: User) => item.id,
  },

  // Empty States
  emptyStates: {
    noData: 'Get started by creating a new user.',
    noSearchResults: 'No users found matching your search criteria.',
    noFilterResults: 'No users match the selected filters.',
  },

  // Delete Confirmation
  delete: {
    title: 'Delete User',
    message: 'Are you sure you want to delete this user? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete user. Please try again.',
    handler: async (id: number) => {
      const { deleteUser } = await import('../../../services/mutations');
      await deleteUser(id);
    }},

  // Utility Functions
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    }},

  // Detail View Configuration
  detail: {
    subtitle: 'User Details',
    notFoundTitle: 'User not found',
    notFoundMessage: 'The user you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Users',
    defaultTitle: 'Unnamed User',
    getTitle: (item: User) => {
      return item.userDisplayName || `${item.userFirstName} ${item.userLastName}`.trim() || item.userEmail || `User #${item.id}`;
    }},

  // Form Configuration
  form: {
    new: {
      title: 'New User',
      subtitle: 'Add a new user to your database',
      submitText: 'Create User'},
    edit: {
      title: 'Edit User',
      submitText: 'Update User'},
    cancelText: 'Cancel',
    loadingText: 'Loading user...',
    errorMessage: 'Failed to save user. Please try again.',
    create: async (data: Partial<User & { userPassword?: string }>) => {
      const { createUser } = await import('../../../services/mutations');
      return await createUser(data);
    },
    update: async (id: number, data: Partial<User & { userPassword?: string }>) => {
      const { updateUser } = await import('../../../services/mutations');
      return await updateUser(id, data);
    }},

  // Unified Field Configuration
  fields: [
    {
      name: 'userEmail',
      label: 'Email',
      type: 'email',
      required: true,
      placeholder: 'Enter email address',
      validation: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address'}},
    {
      name: 'userPassword',
      label: 'Password',
      type: 'text',
      required: true,
      placeholder: 'Enter password',
      validation: {
        required: true,
        minLength: 6,
        message: 'Password must be at least 6 characters'}},
    {
      name: 'userFirstName',
      label: 'First Name',
      type: 'text',
      required: true,
      placeholder: 'Enter first name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255}},
    {
      name: 'userLastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      placeholder: 'Enter last name',
      validation: {
        required: true,
        minLength: 2,
        maxLength: 255}},
    {
      name: 'userDisplayName',
      label: 'Display Name',
      type: 'text',
      placeholder: 'Enter display name (optional)',
      validation: {
        maxLength: 255}},
    {
      name: 'userIsActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Mark as active',
      description: 'Check if this user is active',
      labelPosition: 'left',
      defaultValue: true},
    {
      name: 'typeLocation',
      label: 'Type',
      type: 'select',
      placeholder: 'Select user type',
      required: true,
      options: [
        { value: 'OFFICE', label: 'Office' },
        { value: 'VESSEL', label: 'Vessel' },
        { value: 'SUPPLIER', label: 'Supplier' },
        { value: 'MANNING_AGENT', label: 'Manning agent' },
      ],
      validation: {
        required: true},
      onChangeSideEffect: (value, setFieldValue) => {
        if (value !== 'VESSEL') {
          setFieldValue('vesselId', '');
        }
        if (value !== 'MANNING_AGENT') {
          setFieldValue('manningAgentId', '');
        }
      }},
    {
      name: 'vesselId',
      label: 'Vessel',
      type: 'select',
      placeholder: 'Select vessel',
      apiEndpoint: '/vessels',
      valueField: 'id',
      labelField: 'vesselName',
      options: [{ value: '', label: 'Select vessel' }],
      helperText: 'Required for vessel-type accounts. Used to scope supply data to this vessel.',
      validation: {
        requiredWhen: (fd) => fd.typeLocation === 'VESSEL',
        message: 'Select a vessel for vessel-type users'}},
    {
      name: 'manningAgentId',
      label: 'Manning agent',
      type: 'select',
      placeholder: 'Select manning agent',
      apiEndpoint: '/crew-lookups/manning-agents',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'Select manning agent' }],
      helperText: 'Required for manning-agent accounts. Scopes the crew pool and file to this agency.',
      validation: {
        requiredWhen: (fd) => fd.typeLocation === 'MANNING_AGENT',
        message: 'Select a manning agent for manning-agent users'}},
    {
      name: 'departmentId',
      label: 'Department',
      type: 'select',
      placeholder: 'Select department (optional)',
      apiEndpoint: '/departments',
      valueField: 'id',
      labelField: 'name',
      options: [{ value: '', label: 'No department' }],
      helperText: 'Used to route department-assigned approval steps.'},
    {
      name: 'role',
      label: 'Role',
      type: 'select',
      placeholder: 'Select role',
      required: true,
      options: [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'USER', label: 'User' },
        { value: 'CLIENT', label: 'Client' },
        { value: 'GUEST', label: 'Guest' },
      ],
      validation: {
        required: true}},
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'User Information',
        icon: '👤',
        gridCols: 'md:grid-cols-12',
        fields: ['userEmail', 'userPassword', 'userFirstName', 'userLastName', 'userDisplayName', 'userIsActive', 'typeLocation', 'role', 'departmentId'],
        fieldSpans: {
          userEmail: 'md:col-span-12',
          userPassword: 'md:col-span-12',
          userFirstName: 'md:col-span-6',
          userLastName: 'md:col-span-6',
          userDisplayName: 'md:col-span-12',
          userIsActive: 'md:col-span-4',
          typeLocation: 'md:col-span-4',
          role: 'md:col-span-4',
          departmentId: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'vessel-account',
        title: 'Vessel account',
        icon: '🚢',
        description: 'Shown when Type is Vessel. This vessel is used to filter supply modules for this login.',
        gridCols: 'md:grid-cols-12',
        fields: ['vesselId'],
        fieldSpans: {
          vesselId: 'md:col-span-12'},
        collapsible: false,
        visibleWhen: (formData) => formData.typeLocation === 'VESSEL',
        visibilityDependsOn: ['typeLocation']},
      {
        id: 'manning-agent-account',
        title: 'Manning agent account',
        icon: '👥',
        description: 'Shown when Type is Manning agent. This agency scopes the crew pool and crew file for this login.',
        gridCols: 'md:grid-cols-12',
        fields: ['manningAgentId'],
        fieldSpans: {
          manningAgentId: 'md:col-span-12'},
        collapsible: false,
        visibleWhen: (formData) => formData.typeLocation === 'MANNING_AGENT',
        visibilityDependsOn: ['typeLocation']},
    ]} as FormLayout};

// Theme-aware cell components (must be components to use useTheme)
const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};
const DisplayNameCell: React.FC<{ value: string | null | undefined; row: User }> = ({ value, row }) => {
  const { colors } = useTheme();
  const display = value || `${row.userFirstName || ''} ${row.userLastName || ''}`.trim() || '-';
  return <span className={cn('text-sm', colors.text)}>{display}</span>;
};
const roleBadgeVariant: Record<RoleType, 'accent' | 'info' | 'success' | 'secondary'> = {
  ADMIN: 'accent',
  USER: 'info',
  CLIENT: 'success',
  GUEST: 'secondary'};
const userTypeBadgeVariant: Record<UserTypeLocation, 'info' | 'accent' | 'success'> = {
  OFFICE: 'info',
  VESSEL: 'accent',
  SUPPLIER: 'success',
  MANNING_AGENT: 'info'};
// Same badge style as CRM Tasks status (border, rounded-md, padding)
const statusBadgeClass = 'shrink-0 border rounded-md px-2 py-0.5';

const RoleBadgeCell: React.FC<{ value: RoleType | null | undefined }> = ({ value }) => {
  const variant = value ? roleBadgeVariant[value] : 'default';
  return (
    <Badge variant={variant} className={statusBadgeClass}>
      {value || ''}
    </Badge>
  );
};
const UserTypeBadgeCell: React.FC<{ value: UserTypeLocation | null | undefined }> = ({ value }) => {
  const variant = value ? userTypeBadgeVariant[value] : 'default';
  return (
    <Badge variant={variant} className={statusBadgeClass}>
      {value || ''}
    </Badge>
  );
};
const StatusCell: React.FC<{ value: boolean }> = ({ value }) => (
  <Badge variant={value ? 'success' : 'danger'} className={statusBadgeClass}>
    {value ? 'Active' : 'Inactive'}
  </Badge>
);

// Helper function to get columns
export const getColumns = (formatDate: (dateString?: string) => string): Column<User>[] => [
  {
    key: 'userEmail',
    header: 'Email',
    sortable: true,
    render: (value) => <TextCell value={value} />},
  {
    key: 'userDisplayName',
    header: 'Display Name',
    sortable: true,
    render: (value, row) => <DisplayNameCell value={value} row={row} />},
  {
    key: 'userFirstName',
    header: 'First Name',
    sortable: true,
    render: (value) => <TextCell value={value} />},
  {
    key: 'userLastName',
    header: 'Last Name',
    sortable: true,
    render: (value) => <TextCell value={value} />},
  {
    key: 'role',
    header: 'Role',
    sortable: true,
    render: (value, row) => <RoleBadgeCell value={(value ?? row?.role) as RoleType} />},
  {
    key: 'typeLocation',
    header: 'Type',
    sortable: true,
    render: (value, row) => <UserTypeBadgeCell value={(value ?? row?.typeLocation) as UserTypeLocation} />},
  {
    key: 'vessel.vesselName',
    header: 'Vessel',
    sortable: true,
    render: (_value, row) => (
      <TextCell
        value={
          row?.typeLocation === 'VESSEL' ? row?.vessel?.vesselName || (row?.vesselId ? `#${row.vesselId}` : '') : ''
        }
      />
    )},
  {
    key: 'userIsActive',
    header: 'Status',
    sortable: true,
    render: (value, row) => <StatusCell value={value !== undefined ? !!value : !!(row?.userIsActive)} />},
  {
    key: 'createdAt',
    header: 'Created At',
    sortable: true,
    render: (value) => formatDate(value)},
  {
    key: 'updatedAt',
    header: 'Updated At',
    sortable: true,
    render: (value) => formatDate(value)},
];

// Helper function to get detail sections
export const getDetailSections = (
  item: User,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'User Information',
      fields: [
        {
          label: 'Email',
          value: item.userEmail || '-'},
        {
          label: 'Display Name',
          value: item.userDisplayName || '-'},
        {
          label: 'First Name',
          value: item.userFirstName || '-'},
        {
          label: 'Last Name',
          value: item.userLastName || '-'},
        {
          label: 'Role',
          value: item.role || '-'},
        {
          label: 'Type',
          value: item.typeLocation || '-'},
        {
          label: 'Vessel',
          value:
            item.typeLocation === 'VESSEL'
              ? item.vessel?.vesselName || (item.vesselId != null ? `Vessel #${item.vesselId}` : '—')
              : '—'},
        {
          label: 'Status',
          value: item.userIsActive ? 'Active' : 'Inactive'},
        {
          label: 'Created At',
          value: formatDate(item.createdAt),
          condition: !!item.createdAt},
        {
          label: 'Updated At',
          value: formatDate(item.updatedAt),
          condition: !!item.updatedAt},
      ]},
  ];
};

// Theme-aware status badges for detail header
const StatusBadges: React.FC<{ item: User }> = ({ item }) => {
  const { colors } = useTheme();
  const roleClass: Record<RoleType, string> = {
    ADMIN: colors.accent,
    USER: colors.info,
    CLIENT: colors.success,
    GUEST: cn(colors.surfaceHover, colors.text)};
  return (
    <>
      <span className={cn('inline-flex px-3 py-1 text-sm font-semibold rounded-full', item.userIsActive ? colors.success : colors.error)}>
        {item.userIsActive ? 'Active' : 'Inactive'}
      </span>
      <span className={cn('inline-flex px-3 py-1 text-sm font-semibold rounded-full ml-2', roleClass[item.role] ?? cn(colors.surfaceHover, colors.text))}>
        {item.role}
      </span>
    </>
  );
};
export const getStatusBadges = (item: User) => <StatusBadges item={item} />;

// Helper function to get form sections (legacy, use formLayout instead)
export const getFormSections = () => {
  return [];
};

// Helper function to render toolbar icon
export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'settings':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      );
    case 'download':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      );
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    case 'user-plus':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      );
    default:
      return null;
  }
};

// Table actions - match crm-todos list icon buttons
const TableActionsCell: React.FC<{ item: User; onDelete: (id: number) => void; canDelete?: boolean; canEdit?: boolean }> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  return (
    <div className="flex items-center flex-nowrap">
      <Link
        to={moduleConfig.module.route.detail(item.id)}
        className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link
          to={moduleConfig.module.route.edit(item.id)}
          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canEdit && (
      <Link
        to={`/admin/users/${item.id}/permissions`}
        className="p-1 text-amber-600 hover:text-amber-900 hover:bg-amber-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Permissions"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </Link>
      )}
      {canEdit && (
      <Link
        to={`/admin/users/${item.id}/menu`}
        className="p-1 text-teal-600 hover:text-teal-900 hover:bg-teal-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Customize menu"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      </Link>
      )}
      {canDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};
export const getTableActions = (item: User, onDelete: (id: number) => void, _colors?: unknown, _listContext?: unknown, options?: { canDelete?: boolean; canEdit?: boolean }): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

// Helper function to get initial form data
/** Form model: `vesselId` is string in the select UI; API accepts number. */
export type AdminUserFormModel = Omit<User, 'vesselId' | 'departmentId'> & {
  userPassword?: string;
  vesselId?: number | string | null;
  departmentId?: number | string | null;
};

export const getInitialFormData = (): Partial<AdminUserFormModel> => {
  return {
    userEmail: '',
    userPassword: '',
    userFirstName: '',
    userLastName: '',
    userDisplayName: '',
    userIsActive: true,
    typeLocation: 'OFFICE' as UserTypeLocation,
    role: 'USER' as RoleType,
    vesselId: '',
    departmentId: ''};
};

// Helper function to map item data to form data
export const mapItemToFormData = (item: AdminUserFormModel): Partial<AdminUserFormModel> => {
  const vid = item.vesselId;
  const did = item.departmentId;
  return {
    userEmail: item.userEmail || '',
    userPassword: '', // Don't populate password in edit mode
    userFirstName: item.userFirstName || '',
    userLastName: item.userLastName || '',
    userDisplayName: item.userDisplayName || '',
    userIsActive: item.userIsActive ?? true,
    typeLocation: item.typeLocation || ('OFFICE' as UserTypeLocation),
    role: item.role || 'USER' as RoleType,
    vesselId: vid != null && vid !== '' ? String(vid) : '',
    departmentId: did != null && did !== '' ? String(did) : ''};
};

export type ModuleConfig = typeof moduleConfig;

